import React, {
  useState,
  useEffect,
} from 'react';
import { StripeProvider, Elements } from 'react-stripe-elements';
import {
  List,
  Card,
  Button,
  Menu,
} from 'antd';
import {
  DOCTOR_ID,
} from '../../constants/RoleConstants';
import config from '../../config/configureEnv';

import connectActionsAndReducers from '../../toolbox/connectActionsAndReducers';
import CheckoutForm from '../../components/subscription/CheckoutForm';
import i18n from '../../config/i18n';
import FeaturesView from '../../components/subscription/FeaturesView';
import PaymentHistory from '../../components/subscription/PaymentHistory';

const subscriptionRoleWhitelist = [DOCTOR_ID];

const publicKey = config.stripePublicKey;
const ANNUAL = 'annual';
const MONTHLY = 'monthly';

const styles = {
  center: {
    display: 'flex',
    justifyContent: 'center',
  },
  grid: {
    gutter: 16,
    xs: 1,
    md: 3,
  },
  mainPrice: {
    fontSize: '60px',
  },
  secondaryPrice: {
    fontSize: '30px',
  },
  discountHighlight: {
    border: 'dotted',
    borderColor: '#97defd',
    color: '#1890ff',
    opacity: 0.8,
  },
  centerContents: {
    display: 'flex',
    justifyContent: 'center',
  },
  centerText: {
    textAlign: 'center',
  },
};

const annualPricingData = [
  {
    title: '30 Day Trial',
    features: [
      {
        price: 0,
      },
    ],
    targetPlan: 'boca-monthly-01',
  },
  {
    title: 'Guru Elite',
    features: [
      {
        price: 288,
      },
    ],
    targetPlan: 'boca-annually-01',
  },
  {
    title: 'Guru Pro',
    features: [
      {
        price: 660,
      },
    ],
    targetPlan: 'boca-annually-02',
  },
];

const monthlyPricingData = [
  {
    title: '30 Day Trial',
    features: [
      {
        price: 0,
      },
    ],
    targetPlan: 'boca-monthly-01',
  },
  {
    title: 'Guru Elite',
    features: [
      {
        price: 29.99,
      },
    ],
    targetPlan: 'boca-monthly-02',
  },
  {
    title: 'Guru Pro',
    features: [
      {
        price: 69.99,
      },
    ],
    targetPlan: 'boca-monthly-03',
  },
];

// Plans which can be subscribed without paying but need to be approved by admin.
// Only 'Free Trial' now.
const planWhitelist = [
  'boca-monthly-01',
];

const SubscriptionContainer = ({
  actions,
  actions: {
    getCheckoutToken,
    subscribe,
    createSuccessAlert,
    getPaymentHistory,
  },
  router,
  user: {
    isLoggedIn,
    checkoutToken,
    profile: {
      roleId,
      currentTeamId,
    },
  },
  team: {
    payments,
  },
}) => {
  const [displayPricing, setDisplayPricing] = useState(MONTHLY);

  useEffect(() => {
    if (
      isLoggedIn &&
      subscriptionRoleWhitelist.includes(roleId) &&
      currentTeamId
    ) {
      getPaymentHistory(currentTeamId);
    }
  }, [isLoggedIn, roleId, currentTeamId, getPaymentHistory]);

  const handleClick = async (selectedPlan) => {
    if (isLoggedIn) {
      // Free plan(s).
      if (planWhitelist.find(plan => plan === selectedPlan)) {
        if (await subscribe(selectedPlan)) {
          createSuccessAlert(i18n.t('subscribeSucceedMsg'));
        }
      } else {
        // Get token to pay by Stripe.
        await getCheckoutToken(currentTeamId, selectedPlan);
      }
    } else {
      router.navigate('/signup', { trigger: true });
    }
  };

  return (
    <div>
      {
        isLoggedIn &&
        subscriptionRoleWhitelist.includes(roleId) &&
        <PaymentHistory payments={payments} />
      }
      <div style={{ ...styles.centerContents, ...styles.discountHighlight }}>
        <h4>Save 20% or more by paying annually!</h4>
      </div>
      <div style={styles.centerContents}>
        <Menu
          mode="horizontal"
          style={{ borderBottom: '0px', fontSize: 15 }}
          defaultSelectedKeys={[displayPricing]}
          onSelect={(item) => { setDisplayPricing(item.key); }}
        >
          <Menu.Item key={ANNUAL}>Annual Pricing</Menu.Item>
          <Menu.Item key={MONTHLY}>Monthly Pricing</Menu.Item>
        </Menu>
      </div>
      <br />
      <List
        grid={styles.grid}
        dataSource={displayPricing === ANNUAL ? annualPricingData : monthlyPricingData}
        renderItem={item => (
          <List.Item>
            <Card title={(<h3 style={styles.center}>{item.title}</h3>)}>
              <List
                itemLayout="vertical"
                dataSource={item.features}
                renderItem={el => (
                  <List.Item>
                    <div style={styles.center}>
                      {el.price === 0
                        ? (<div style={styles.centerText}>
                          <span style={styles.mainPrice}>Free</span>
                          <br />
                          {/* To make items have the same height,
                             we add a transparent text here. */}
                          <span style={{ ...styles.secondaryPrice, color: 'rgba(0, 0, 0, 0)' }}>.</span>
                        </div>)
                        : (<div style={styles.centerText}>
                          <span style={styles.secondaryPrice}>$</span>
                          <span style={styles.mainPrice}>{el.price}</span>
                          <br />
                          <span style={styles.secondaryPrice}>
                            {displayPricing === ANNUAL ? '/billed yearly' : '/provider/month'}</span>
                        </div>)}
                    </div >
                  </List.Item>
                )}
              />
              <br />
              <div style={styles.center}>
                <Button
                  size="large"
                  type="primary"
                  onClick={() => handleClick(item.targetPlan)}
                  disabled={isLoggedIn && !subscriptionRoleWhitelist.includes(roleId)}
                >
                  {i18n.t('tryout')}
                </Button>
              </div>
            </Card>
          </List.Item>
        )}
      />
      {
        checkoutToken && (
          <StripeProvider apiKey={publicKey}>
            <Elements>
              <CheckoutForm
                actions={actions}
                checkoutToken={checkoutToken}
              />
            </Elements>
          </StripeProvider>
        )
      }
      <FeaturesView />
    </div >
  );
};

export default connectActionsAndReducers(SubscriptionContainer);



// WEBPACK FOOTER //
// ./src/containers/subscription/SubscriptionContainer.js